import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Box, Container, Typography, Grid, Stack, Divider, Chip,
  Button,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { PhoneCallIcon } from "lucide-react";

const services = [
  {
    title: "Land + Build Packages",
    desc: "Complete land and build contracts under one streamlined agreement. Ideal for first-home buyers, families, and investors looking for a simple, stress-free solution.",
    icon: "🏗️",
  },
  {
    title: "Custom Home Builds",
    desc: "Fully custom single-storey and double-storey homes tailored to your lifestyle, budget, and block. No standard designs — every home is built specifically for you.",
    icon: "🏠",
  },
  {
    title: "Only Build Services",
    desc: "Already have land and approved plans? We provide build-only services, working with your architect or designer to deliver your home exactly as planned.",
    icon: "📐",
  },
  {
    title: "Renovations & Extensions",
    desc: "From major renovations to extensions and upgrades, we help homeowners transform existing properties into modern, functional living spaces.",
    icon: "🔨",
  },
  {
    title: "Granny Flats",
    desc: "High-quality granny flats for families, rental income, or multi-generational living, designed to meet NSW regulations and maximise space.",
    icon: "🏡",
  },
  {
    title: "Complex & Multi-Storey Builds",
    desc: "Multi-storey dwelling houses, large custom homes, and challenging sites with detailed architectural designs — managed with confidence.",
    icon: "🏢",
  },
];

const process = [
  { step: "01", title: "Initial Consultation", desc: "Understand your goals, land, and budget." },
  { step: "02", title: "Design & Planning", desc: "Custom or client-supplied designs." },
  { step: "03", title: "Approvals", desc: "Support with CDC or DA approvals." },
  { step: "04", title: "Construction", desc: "Quality workmanship with clear communication." },
  { step: "05", title: "Handover", desc: "A finished home built to Australian standards." },
];

const whyUs = [
  "500+ homes built across NSW",
  "Land & build, build-only, and renovation services",
  "Granny flats and complex multi-storey homes",
  "Fully custom residential builds",
  "Experienced, reliable construction team",
  "Honest advice and transparent processes",
];

const stats = [
  { value: "500+", label: "Homes Built" },
  { value: "NSW", label: "Wide Coverage" },
  { value: "100%", label: "Custom Builds" },
  { value: "15+", label: "Years Experience" },
];

export default function About() {
  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
  }, []);

  return (
    <Box sx={{  bgcolor: "#FAFAF8", color: "#1a1a1a" }}>

      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: "var(--primary-color)",
          color: "#fff",
          py: { xs: 10, md: 14 },
          position: "relative",
          overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: "-80px", right: "-80px",
            width: "320px", height: "320px",
            borderRadius: "50%",
            border: "70px solid rgba(192,174,93,0.07)",
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={3} maxWidth={680}>
            <Box data-aos="fade-right">
              <Chip
                label="About Clarence Homes"
                sx={{ border: " 2px solid var(--primary-text-color)", color: "#fff", fontWeight: 600, mb: 2, fontSize: "0.75rem", letterSpacing: 1 }}
              />
              <Typography
                variant="h2"
                fontWeight={700}
                lineHeight={1.15}
                sx={{ fontSize: { xs: "2.2rem", md: "3.5rem" } }}
              >
                Over 500 Homes Built Across NSW
              </Typography>
            </Box>
            <Typography
              data-aos="fade-right"
              data-aos-delay="150"
              variant="body1"
              sx={{ color: "rgba(255,255,255,0.75)", fontSize: "1.1rem", lineHeight: 1.8 }}
            >
              Clarence Homes is a full-service residential building company specialising in custom home building,
              land & build packages, renovations, granny flats, and complex multi-storey residential projects.
            </Typography>
            <Box data-aos="fade-right" data-aos-delay="250">
              <Divider sx={{ borderColor: "var(--primary-text-color)", width: 60, borderBottomWidth: 3 }} />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Stats Bar */}
      <Box sx={{ bgcolor: "var(--primary-text-color)", py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s, i) => (
              <Grid item xs={6} md={3} key={i} data-aos="zoom-in" data-aos-delay={i * 100}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="h4" fontWeight={800} color="#fff">
                    {s.value}
                  </Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.85)" fontWeight={600} letterSpacing={1} textTransform="uppercase">
                    {s.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Who We Are */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6} data-aos="fade-right">
            <Stack spacing={3}>
              <Typography className="fw-bold" color="var(--primary-color)" fontWeight={700}>
                Who We Are
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                Experience, Quality & Reliable Project Management
              </Typography>
              <Typography variant="body1" color="text.secondary" lineHeight={1.9}>
                From simple builds to technically complex homes, Clarence Homes brings decades of hands-on experience
                to every project. We understand NSW council requirements, CDC and DA processes, and the importance
                of quality construction that stands the test of time.
              </Typography>
              <Typography variant="body1" color="text.secondary" lineHeight={1.9}>
                Our reputation is built on quality, experience, and trust — delivering homes that exceed expectations
                across Sydney and regional New South Wales.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} data-aos="fade-left">
            <Box
              sx={{
                bgcolor: "var(--primary-color)",
                borderRadius: 4,
                p: 5,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -12, right: -12,
                  width: "100%", height: "100%",
                  border: "2px solid var(--primary-color)",
                  borderRadius: 4,
                  zIndex: -1,
                },
              }}
            >
              <HomeWorkIcon sx={{ fontSize: 48, color: "var(--primary-text-color)", mb: 2 }} />
              <Typography variant="h3" fontWeight={800} color="#fff">
                500+
              </Typography>
              <Typography color="rgba(255,255,255,0.7)" variant="h6" mb={3}>
                Homes Delivered Across NSW
              </Typography>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", mb: 3 }} />
              <Typography color="rgba(255,255,255,0.6)" lineHeight={1.8}>
                Every project — from a compact granny flat to a complex multi-storey custom home — is delivered
                with the same commitment to quality and clear communication.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Services */}
      <Box sx={{ bgcolor: "#F2F0EB", py: { xs: 8, md: 12 } }}>
        <Box className='p-3'>
          <Stack alignItems="center" spacing={1} mb={8} data-aos="fade-up">
            <Typography color="var(--primary-color)" fontWeight={700} letterSpacing={2}>
              What We Offer
            </Typography>
            <Typography variant="h4" fontWeight={700} textAlign="center">
              Complete Residential Building Solutions
            </Typography>
          </Stack>
          <Grid container data-aos="fade-up" spacing={3}>
            {services.map((s, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 3,
                    p: 4,
                    height: "100%",
                    border: "1px solid transparent",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "var(--primary-text-color)",
                      transform: "translateY(-6px)",
                      boxShadow: "0 10px 36px rgba(19,25,83,0.13)",
                    },
                  }}
                >
                  <Typography fontSize="2rem" mb={2}>{s.icon}</Typography>
                  <Typography variant="h6" fontWeight={700} mb={1.5}>
                    {s.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                    {s.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Process */}
      <Box className="p-3" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack alignItems="center" spacing={1} mb={8} data-aos="fade-up">
          <Typography variant="overline" color="var(--primary-color)" fontWeight={700} letterSpacing={2}>
            Our Process
          </Typography>
          <Typography variant="h4" fontWeight={700} textAlign="center">
            How We Build Your Home
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          {process.map((p, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i} data-aos="fade-up" data-aos-delay={i * 100}>
              <Stack direction="row" spacing={3} alignItems="flex-start">
                <Typography
                  sx={{
                    fontSize: "3rem", fontWeight: 800, color: "var(--primary-light-text-color)",
                    lineHeight: 1, minWidth: 60,
                  }}
                >
                  {p.step}
                </Typography>
                <Stack spacing={0.5} pt={0.5}>
                  <Typography variant="h5" fontWeight={700}>
                    {p.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                    {p.desc}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Why Choose Us */}
      <Box sx={{ bgcolor: "var(--primary-text-color)", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5} data-aos="fade-right">
              <Stack spacing={2}>
                <Typography variant="overline" color="var(--primary-color)" fontWeight={700} letterSpacing={2}>
                  Why Choose Us
                </Typography>
                <Typography variant="h4" fontWeight={700} color="black" sx={{  lineHeight: 1.3 }}>
                  Built on Quality, Experience & Trust
                </Typography>
                <Typography color="black" lineHeight={1.9}>
                  We manage the entire journey so you can build with confidence — from initial consultation through to handover.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={7} data-aos="fade-left">
              <Grid container spacing={2}>
                {whyUs.map((item, i) => (
                  <Grid item xs={12} sm={6} key={i} data-aos="fade-up" data-aos-delay={i * 80}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: "var(--primary-color)", mt: 0.3, flexShrink: 0 }} />
                      <Typography color="black" variant="body1" lineHeight={1.7}>
                        {item}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Where We Build */}
      <Box sx={{ bgcolor: "#F2F0EB", py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Stack alignItems="center" spacing={3} textAlign="center" data-aos="fade-up">
            <LocationOnIcon sx={{ fontSize: 48, color: "var(--primary-color)" }} />
            <Typography variant="overline" color="var(--primary-color)" fontWeight={700} letterSpacing={2}>
              Where We Build
            </Typography>
            <Typography variant="h4" fontWeight={700} >
              Servicing All of NSW
            </Typography>
            <Typography color="text.secondary" maxWidth={560} lineHeight={1.9}>
              Clarence Homes builds across Sydney and regional New South Wales, delivering residential projects
              that meet local council requirements and client expectations.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ bgcolor: "var(--primary-color)", py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Stack alignItems="center" spacing={3} textAlign="center" data-aos="zoom-in">
            <Typography variant="h4" fontWeight={700} color="#fff">
              Start Your Project With Clarence Homes
            </Typography>
            <Typography color="rgba(255,255,255,0.85)" maxWidth={560} lineHeight={1.9}>
              Whether you're planning a land & build package, a custom home, a granny flat, or a complex
              multi-storey residential build — we have the experience to deliver.
            </Typography>
            <Button component="a" href="tel:1300451780" variant='outlined' fontSize={{ xs: "12px", md: "16px" }} className='fs-14  btn-outlined-second  border-20'><PhoneCallIcon /> &nbsp; 1300 451 780</Button>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}